import React, { useState, useEffect } from "react";
import { Button, Card, Container, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Stack } from "@mui/material";
import { Page } from "../../../components";
import HeaderBreadcrumbs from "../../../components/header-breadcrumbs";
import { GroupForm } from "../../../components/groups-and-teams/group-form/group-form";
import { useSelector } from "react-redux";
import { RootState } from "../../../store";
import { GroupService } from "../../../services/groups.service";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";

export const GroupFormPage: React.FC = () => {
    const navigate = useNavigate();
    const { groupId } = useParams<{ groupId: string }>();
    const [confirmOpen, setConfirmOpen] = useState(false);
    const {
        selectedGroup
    } = useSelector((state: RootState) => state.groups);

    const isEditMode = !!groupId;

    // Load group data if we're in edit mode
    useEffect(() => {
        if (isEditMode && groupId) {
            GroupService.fetchGroupById(groupId, (isValid, message) => {
                if (!isValid) {
                    toast.error(message || "Failed to load group");
                    navigate('/groups-and-teams');
                }
            });
        } else {
            // Clear selected group for create mode
            GroupService.setGroup(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [groupId, isEditMode]);

    const handleDelete = async () => {
        if (!selectedGroup || !isEditMode) return;
        setConfirmOpen(false);
        await GroupService.deleteGroup(selectedGroup.id!, (isValid) => {
            if (isValid) {
                toast.success("Group deleted successfully");
                GroupService.setGroup(null);
                navigate('/groups-and-teams');
            } else {
                toast.error("Failed to delete Group");
            }
        });
    };

    return (
        <Page title={isEditMode ? 'Edit Group' : 'Create Group'}>
            <Container maxWidth={false}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <HeaderBreadcrumbs
                        heading={isEditMode ? 'Edit Group' : 'Create Group'}
                        links={[
                            {
                                name: 'Groups & Teams',
                                href: '/groups-and-teams'
                            },
                            {
                                name: isEditMode ? 'Edit Group' : 'Create Group',
                                href: isEditMode ? `/groups/${groupId}/edit` : '/groups/create'
                            }
                        ]}
                        activeLast={false}
                    />
                    {isEditMode && selectedGroup && (
                        <Button
                            variant="contained"
                            color="error"
                            onClick={() => setConfirmOpen(true)}
                        >
                            Delete
                        </Button>
                    )}
                </Stack>
                <Card sx={{ mt: 2, height: '100%' }}>
                    <GroupForm />

                </Card>
                {/* Confirm Delete Dialog */}
                <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
                    <DialogTitle>Confirm Delete</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            Are you sure you want to delete this group and all associated teams? This action cannot be undone.
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
                        <Button onClick={handleDelete} color="error" variant="contained">
                            Delete
                        </Button>
                    </DialogActions>
                </Dialog>
            </Container>
        </Page>
    );
};
